import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useCodeTornei } from '@/contexts/CodeTorneiContext';
import { useCollegamentoProfilo } from '@/hooks/useCollegamentoProfilo';
import { serveCrearloProfilo, useProfiloPerSfida } from '@/hooks/useProfiloPerSfida';
import { useSyncProfiloLimulo } from '@/hooks/useSyncProfiloLimulo';
import { annuncia } from '@/lib/a11y/annuncio';
import {
  CHALLENGE_REASONS_CHIUSA,
  esitoDaSyncAlTocco,
  esitoProfiloNonCollegato,
  profileIdDellaSfida,
  rifiutoAccettaSfidaOf,
  type EsitoRifiutoSfida,
} from '@/lib/limulo/challenge';
import { challengeReasonOf, limuloFailure, profileReasonOf } from '@/lib/limulo/errors';
import { attesaProfili, esitoSyncDi, profiloPerIscrizione } from '@/lib/limulo/registration';
import { segnaSfidaGestita } from '@/lib/limulo/sfideGestite';
import { prendiSfidaInVolo, rilasciaSfidaInVolo, staSfidaInVolo, useSfideInVolo } from '@/lib/limulo/sfideInVolo';
import {
  useAcceptLimuloChallengeMutation,
  useDeclineLimuloChallengeMutation,
  useGetLimuloProfilesQuery,
} from '@/services/limuloApi';
import type { LimuloChallengeWithIdentities } from '@/types/limulo';

/**
 * **Rispondere a una sfida di piattaforma in arrivo, in UN posto solo**: lo usano la riga della barra
 * (`contexts/SorgenteSfidePiattaforma`, lotto B1) e la riga del tab «Sfide» (lotto B2). Due superfici, una
 * sola regola: la scelta del profilo, le frasi d'esito, `gestite` e la guardia anti-doppio-tap.
 * 🔑 **La guardia «in volo» e' CONDIVISA fra le superfici** (`lib/limulo/sfideInVolo`, store di modulo): sulla
 * stessa sfida barra e tab hanno due coppie di pulsanti indipendenti (su `/friends` la barra mostra la riga di
 * piattaforma, che non ha destinazione), e una guardia per istanza lasciava passare un secondo tocco dall'altra
 * come una seconda POST. Con la guardia di modulo, un tocco in volo spegne i pulsanti su TUTTE e due.
 * ⚠️ Per il resto estratto da `SorgenteSfidePiattaforma` SENZA cambiare comportamento (lo provano i suoi banchi).
 */

/** Quanto resta a schermo l'esito di un tocco (accettata, rifiutata, «non c'e' piu'»). Una SCELTA, non una misura. */
export const AVVISO_MS = 5_000;

/**
 * Cio' che il giocatore ha saputo dal suo ultimo tocco su una sfida: `ok` (accettata/rifiutata), `chiusa`
 * (il motore dice che non c'e' piu': scaduta, gia' chiusa, sparita) o `errore` (un guasto, ritentabile).
 * `testo` e' la frase gia' localizzata.
 */
export type TipoNota = 'ok' | 'chiusa' | 'errore';
export interface Nota {
  sfida: LimuloChallengeWithIdentities;
  testo: string;
  tipo: TipoNota;
}

export type AnnotaSfida = (sfida: LimuloChallengeWithIdentities, nota: Omit<Nota, 'sfida'>) => void;

/** La frase di un esito negativo: quella del suo motivo, o quella generica col codice in chiaro. */
export function testoEsito(
  t: (k: string, v?: Record<string, unknown>) => string,
  esito: EsitoRifiutoSfida,
  chiaveGenerica: string,
): string {
  if (esito.kind === 'profilo') return t(esito.chiave, { reason: esito.ragione, code: esito.codice });
  return esito.kind === 'nota'
    ? t(esito.chiave)
    : t(chiaveGenerica, {
        code: esito.codice !== '' ? esito.codice : t('social.sfida_rifiuto_sconosciuto', { defaultValue: 'unknown' }),
      });
}

/**
 * Cio' che il giocatore HA GIA' FATTO: sta nel PADRE e non nella riga, perche' la riga sparisce quando la
 * sfida esce dalla lista (l'Accetta invalida la lista, che rilegge subito; un 503 puo' nascondere
 * un'accettazione riuscita), e con lei sparirebbe l'unico segnale che il tocco e' andato — «chi vede non la
 * legge» (bloccante 3 di #489).
 *  · `gestite` (store di MODULO, `lib/limulo/sfideGestite`): le sfide su cui il giocatore ha agito, per
 *    tutta la pagina — sopravvive a un rimontaggio. Una lettura ancora vecchia che le dice `pending` non le
 *    rimette in offerta.
 *  · `note`: l'esito, VISIBILE nella riga. `ok`/`chiusa` restano `AVVISO_MS` e chiudono la sfida;
 *    `errore` (un guasto: la sfida puo' essere ancora li') resta finche' la riga e' viva e, se la sfida
 *    esce dalla lista, `AVVISO_MS` da quel momento.
 *
 * @param viveIds le sfide ancora offerte (in lista e non gestite): un errore la cui sfida non c'e' piu' e'
 *   un'accettazione riuscita nonostante il guasto, e resta leggibile `AVVISO_MS`.
 */
export function useEsitiSfide(viveIds: ReadonlySet<string>) {
  const [note, setNote] = useState<Readonly<Record<string, Nota>>>({});
  const timer = useRef(new Map<string, number>());
  useEffect(() => {
    const attivi = timer.current;
    return () => {
      for (const id of attivi.values()) window.clearTimeout(id);
      attivi.clear();
    };
  }, []);
  const togli = useCallback((challengeId: string) => {
    window.clearTimeout(timer.current.get(challengeId));
    timer.current.delete(challengeId);
    setNote((prev) => {
      if (!(challengeId in prev)) return prev;
      const resto = { ...prev };
      delete resto[challengeId];
      return resto;
    });
  }, []);
  const avviaTimer = useCallback(
    (challengeId: string) => {
      window.clearTimeout(timer.current.get(challengeId));
      timer.current.set(challengeId, window.setTimeout(() => togli(challengeId), AVVISO_MS));
    },
    [togli],
  );
  const annota = useCallback<AnnotaSfida>(
    (sfida, nota) => {
      const { challengeId } = sfida;
      setNote((prev) => ({ ...prev, [challengeId]: { sfida, ...nota } }));
      if (nota.tipo === 'errore') {
        window.clearTimeout(timer.current.get(challengeId));
        timer.current.delete(challengeId);
        return;
      }
      segnaSfidaGestita(challengeId);
      // Il successo non ha un `role="alert"` come l'errore: lo annuncia anche la live region condivisa.
      if (nota.tipo === 'ok') annuncia(nota.testo);
      avviaTimer(challengeId);
    },
    [avviaTimer],
  );

  // Un errore la cui sfida non e' piu' in lista (accettata davvero, nonostante il guasto): resta leggibile
  // `AVVISO_MS`, poi se ne va.
  const orfaniErrore = Object.values(note)
    .filter((n) => n.tipo === 'errore' && !viveIds.has(n.sfida.challengeId))
    .map((n) => n.sfida.challengeId)
    .sort()
    .join(',');
  useEffect(() => {
    for (const id of orfaniErrore === '' ? [] : orfaniErrore.split(',')) {
      if (!timer.current.has(id)) avviaTimer(id);
    }
  }, [orfaniErrore, avviaTimer]);

  return { note, annota, togli };
}

/**
 * Il rinnovo immediato del biglietto di coda dopo un accept che il motore ha risposto (200 o 409 «abbinato in coda»).
 * Non lancia MAI: e' un aiuto, e nessun suo guasto puo' cambiare cio' che il giocatore legge (la nota e' decisa altrove).
 * Se lancia lo DICE (solo il nome dell'errore, mai l'oggetto: puo' portare dati): l'unica cosa che puo' prendere e' un errore di
 * cablaggio, e tacerlo lo nasconderebbe.
 */
function rinnovaDopoAccept(rinnovaOra: (gameId: string) => void, gameId: string): void {
  try {
    rinnovaOra(gameId);
  } catch (e) {
    // La nota a schermo non si tocca, e il ciclo di rinnovo dei 20 s resta la rete: ma il guasto non passa in silenzio.
    console.error('[useRispostaSfida] rinnovaOra ha lanciato', e instanceof Error ? e.name : typeof e);
  }
}

/**
 * I tocchi di UNA sfida in arrivo. Il profilo con cui si accetta e' la scelta di
 * `useInvioSfidaPiattaforma` (`profiloPerIscrizione` + `attesaProfili` + `profileIdDellaSfida`): il
 * motore lo vuole anche da chi accetta, e senza una lista ARRIVATA non si manda `null` — sarebbe un
 * «ti manca il profilo» detto a chi ce l'ha.
 *
 * @param errore l'errore dell'ultimo tocco: lo tiene il padre (`useEsitiSfide`), la riga lo riceve.
 */
export function useRispostaSfida(sfida: LimuloChallengeWithIdentities, annota: AnnotaSfida, azzera: (challengeId: string) => void) {
  const { t } = useTranslation('general');
  const profili = useGetLimuloProfilesQuery(undefined);
  // I-493: chi accetta senza essere mai passato da una scheda torneo di quel gioco ha un profilo senza `sub`
  // del provider e prende `sub_missing`. STESSO hook e STESSA regola delle schede (una volta per profilo e per
  // sessione, solo schemi a zero campi, fire-and-forget). Kb#388: l'Accetta lo aspetta SOLO se e' in volo o caduto
  // (`useCollegamentoProfilo`, uno solo per tocco, col tetto), mai all'apertura. Piu' righe sullo stesso
  // gioco (barra, tab, DM) condividono la cache RTK e il Set di modulo: parte UN sync.
  useSyncProfiloLimulo(sfida.gameId);
  const [accettaSfida, accettazione] = useAcceptLimuloChallengeMutation();
  const [rifiutaSfida, rifiuto] = useDeclineLimuloChallengeMutation();
  // La guardia anti-doppio-tap e' di MODULO (v. il docblock): `disabled` e' un suggerimento, questa e' la regola.
  const inVoloAdesso = useSfideInVolo();
  // Sola lettura: `rinnovaOra` e' stabile (legge gioco e ciclo da ref) e fuori dal provider e' una funzione vuota.
  // ⚠️ DEVE restare stabile: se diventasse nuova a ogni render, `onAccetta` cambierebbe, la riga della barra si ricalcolerebbe, il
  // provider rirenderizzerebbe e il giro si ripeterebbe fino all'heap OOM (lo stesso anello di `scelta`, qui sotto). I banchi
  // che fingono `useCodeTornei` le danno una funzione STABILE (una `vi.fn` hoisted), mai una nuova a ogni chiamata.
  const { rinnovaOra } = useCodeTornei();
  // I-543: il profilo che manca si crea al tocco di Accetta, se lo schema del gioco non chiede niente.
  const preparaProfilo = useProfiloPerSfida();
  // kb#388: col profilo gia' li', il tocco aspetta il sync in volo (o rifatto, se era caduto) invece di correre al rifiuto.
  const { collega, collegando } = useCollegamentoProfilo();

  // ⚠️ `useMemo`: `profiloPerIscrizione` rende un oggetto NUOVO a ogni chiamata, e `scelta` sta nelle
  // dipendenze di `onAccetta` ⇒ della riga ⇒ dell'effetto che la riporta: senza, ogni render riporta una
  // riga nuova, il provider rirenderizza e il ciclo non finisce.
  const dati = profili.data;
  const scelta = useMemo(() => profiloPerIscrizione(dati, sfida.gameId), [dati, sfida.gameId]);
  const profiliInErrore = profili.isError;
  const inLettura = attesaProfili(scelta, profiliInErrore) === 'in-lettura';
  const rileggiProfili = profili.refetch;

  const { challengeId } = sfida;
  const finita = useCallback(
    (e: unknown, chiaveGenerica: string, profileId: string | null) => {
      const failure = limuloFailure(e);
      const motivo = challengeReasonOf(e);
      // kb#388: `profile.not-linked` letto con l'ultimo esito del sync di QUEL profilo ⇒ la frase col gesto vero; senza esito
      // utile (sync ok o mai chiesto, o un altro motivo-profilo) cade sulla frase di sempre.
      const nonCollegato = esitoProfiloNonCollegato(profileReasonOf(e), profileId === null ? undefined : esitoSyncDi(profileId));
      const esito = nonCollegato ?? rifiutoAccettaSfidaOf(motivo, failure.code ?? String(failure.status ?? ''));
      const testo = testoEsito(t, esito, chiaveGenerica);
      // La sfida non c'e' piu' (scaduta, gia' chiusa, sparita): niente da ritentare, la frase resta leggibile.
      // Ogni altro guasto: la riga resta offerta, e la frase sta nel padre — se la sfida sparisce dalla lista
      // (un 503 dopo un'accettazione riuscita), non sparisce muta con lei.
      const chiusa = motivo !== null && CHALLENGE_REASONS_CHIUSA.has(motivo);
      annota(sfida, { testo, tipo: chiusa ? 'chiusa' : 'errore' });
    },
    [annota, sfida, t],
  );

  /** L'accettazione vera, col profilo deciso. Il tocco ha GIA' preso la guardia «in volo»: questa la rilascia a risposta data. */
  const accettaConProfilo = useCallback(
    (profileId: string | null) => {
      const risposta = accettaSfida({ challengeId, profileId }).unwrap();
      risposta
        // Esce SUBITO dall'offerta (`gestite`) e il tocco ha il suo segnale visibile (l'avviso): la lista si
        // rilegge da sola (`acceptLimuloChallenge` invalida), ma dopo — e la riga non deve restare offerta.
        .then(() => annota(sfida, { testo: t('social.sfida_accetta_ok'), tipo: 'ok' }))
        .catch((e: unknown) => finita(e, 'social.sfida_accetta_generico', profileId))
        .finally(() => rilasciaSfidaInVolo(challengeId));
      // Il motore ha appena cambiato il biglietto di coda di chi accetta (200: lo toglie dalla coda; 409 «abbinato in
      // coda»: era gia' `matched`): si rinnova SUBITO, senza aspettare i 20 s, cosi' le altre righe e superfici non
      // raccontano un'attesa che non c'e' piu'. ⛔ In un ramo SEPARATO della stessa risposta, non dentro la catena che
      // decide la nota: un suo lancio non puo' cambiare a schermo «accettata» o la frase del 409. ⛔ Da questa risposta,
      // mai da un effetto che dipende da `ticket`: girerebbe a ogni risposta di rinnovo. Solo 200 e 409 abbinato: non sugli
      // altri errori (il biglietto non e' cambiato) e non su Rifiuta.
      risposta.then(
        () => rinnovaDopoAccept(rinnovaOra, sfida.gameId),
        (e: unknown) => {
          if (challengeReasonOf(e) === 'sfida.abbinato-in-coda') rinnovaDopoAccept(rinnovaOra, sfida.gameId);
        },
      );
    },
    [accettaSfida, annota, challengeId, finita, rinnovaOra, sfida, t],
  );

  const onAccetta = useCallback(() => {
    if (staSfidaInVolo(challengeId)) return;
    const attesa = attesaProfili(scelta, profiliInErrore);
    if (attesa !== 'pronti') {
      if (attesa === 'illeggibili') {
        annota(sfida, { testo: t('social.sfida_profili_illeggibili'), tipo: 'errore' });
        void rileggiProfili();
      }
      return;
    }
    // ⚠️ La guardia «in volo» si prende PRIMA di qualunque attesa: con un profilo da creare il tocco passa da una
    // promessa, e un secondo tocco (o la stessa sfida dall'altra superficie) non deve creare un secondo profilo
    // ne' mandare una seconda accettazione.
    if (!prendiSfidaInVolo(challengeId)) return;
    azzera(challengeId);
    // Un profilo c'e' (o la sfida non ha un gioco): come prima, SUBITO e senza una microtask in mezzo — salvo che un sync del
    // suo profilo sia in volo o caduto (kb#388): allora il tocco lo aspetta (uno solo) e la sfida parte solo se e' andato.
    if (!serveCrearloProfilo(scelta, sfida.gameId)) {
      const profileId = profileIdDellaSfida(scelta);
      collega(profileId, (collegamento) => {
        if (collegamento.kind === 'ok') {
          accettaConProfilo(profileId);
          return;
        }
        annota(sfida, { testo: testoEsito(t, esitoDaSyncAlTocco(collegamento), 'social.sfida_accetta_generico'), tipo: 'errore' });
        rilasciaSfidaInVolo(challengeId);
      });
      return;
    }
    // I-543: nessun profilo per il gioco della sfida. Se lo schema non chiede niente il profilo nasce ORA, al tocco, e
    // l'accettazione parte col suo id; con campi parte com'e' sempre (il motore dira' `profilo-richiesto`).
    void preparaProfilo(sfida.gameId, scelta).then((profilo) => {
      if (profilo.kind === 'pronto') {
        accettaConProfilo(profilo.profileId);
        return;
      }
      // ⛔ MAI un'accettazione senza il profilo che si doveva creare: la riga resta offerta, con il suo motivo.
      annota(sfida, {
        testo:
          profilo.kind === 'non-creato'
            ? t('social.sfida_accetta_profilo_non_creato', { reason: profilo.ragione })
            : profilo.kind === 'senza-id'
              ? t('social.sfida_accetta_profilo_senza_id')
              : profilo.kind === 'collegamento'
                ? testoEsito(t, esitoDaSyncAlTocco(profilo.esito), 'social.sfida_accetta_generico')
                : t('social.sfida_profili_illeggibili'),
        tipo: 'errore',
      });
      rilasciaSfidaInVolo(challengeId);
    });
  }, [accettaConProfilo, annota, azzera, challengeId, collega, preparaProfilo, profiliInErrore, rileggiProfili, scelta, sfida, t]);

  const onRifiuta = useCallback(() => {
    if (!prendiSfidaInVolo(challengeId)) return;
    azzera(challengeId);
    rifiutaSfida({ challengeId })
      .unwrap()
      .then(() => annota(sfida, { testo: t('social.sfida_rifiuta_ok'), tipo: 'ok' }))
      .catch((e: unknown) => finita(e, 'social.sfida_rifiuta_generico', null))
      .finally(() => rilasciaSfidaInVolo(challengeId));
  }, [azzera, challengeId, finita, annota, rifiutaSfida, sfida, t]);

  // Un tocco in volo dall'ALTRA superficie spegne anche questa (per questa istanza non c'e' `isLoading`).
  const daAltrove = inVoloAdesso.has(challengeId) && !accettazione.isLoading && !rifiuto.isLoading;
  return {
    onAccetta,
    onRifiuta,
    accettando: accettazione.isLoading || daAltrove,
    rifiutando: rifiuto.isLoading,
    /** La lista dei profili in lettura tiene spento Accetta come un'accettazione in corso. */
    inLettura,
    /** kb#388: il tocco sta aspettando il sync del profilo — la superficie lo dice («Sto collegando il tuo profilo…»). */
    collegando,
  };
}
